























































































































































































































































































































import Vue from 'vue'
import * as R from 'ramda'
import { mapActions, mapGetters } from 'vuex'
import { ModuleNames } from '@/store/module-names'
import { DetailedPatientActionNames, DetailedPatientGetterNames } from '@/store/modules/detailed-patient/keys'
import { getCareGiverName, getPatientName } from '@/utils/patients'
import { toReadableTimeFromTimestamp, withDefault, withUnit } from '@/components/exercises/measure/filters'
import { DEFAULT_TIME_FORMAT } from '@/utils/time'
import { FlatDetailedPatient } from '@/store/modules/detailed-patient/getters'
import { Inhaler, LungCapacity, MedicalInfo, Prescription, SixMinutesWalk } from '@/jsonapi/DetailedPatient'

export default Vue.extend({
  filters: {
    toReadableTimeFromTimestamp,
    withUnit,
    withDefault,
  },
  data() {
    return {
      loading: true as boolean,
      defaultTimeFormat: DEFAULT_TIME_FORMAT as string,
    }
  },
  computed: {
    ...mapGetters(ModuleNames.detailedPatient, [DetailedPatientGetterNames.patients]),
    patientId(): string {
      return this.$route.params.patient_id
    },
    patient(): FlatDetailedPatient | null {
      return this[DetailedPatientGetterNames.patients].find(p => p.id === this.patientId)
    },
    fullName(): string {
      return this.patient ? getPatientName(this.patient) : ''
    },
    medicalInfo(): MedicalInfo {
      return {
        ...{ sixMinutesWalks: [], lungCapacities: [], erVisit: [{}] },
        ...R.pathOr({}, ['medicalInfo'], this.patient),
      }
    },
    address(): string {
      const address = R.pathOr('', ['patient', 'address'], this)
      if (!address) {
        return withDefault('')
      }

      const { street, city, province, country, postalCode } = address
      const label = [street, [city, province, country, postalCode].filter(Boolean).join(', ')]
        .filter(Boolean)
        .join('<br>')

      return withDefault(label)
    },
    careGiverName(): string {
      if (!this.patient) {
        return ''
      }
      return this.patient.mainCareGiver
        ? getCareGiverName(this.patient.mainCareGiver)
        : this.patient.otherCareGivers || ''
    },
    sixMinuteWalksTableData(): { headers: Array<any>; items: SixMinutesWalk[] } {
      return {
        headers: [
          {
            text: 'Distance (m)',
            value: 'distance',
            width: '25%',
            sortable: true,
          },
          {
            text: 'Test Date',
            value: 'timestamp',
            sortable: true,
          },
        ],
        items: this.medicalInfo.sixMinutesWalks,
      }
    },
    lungCapacityTableData(): { headers: Array<any>; items: LungCapacity[] } {
      return {
        headers: [
          {
            text: 'FEV1 (L)',
            value: 'fev1',
            width: `${(2.25 / 12) * 100}%`,
            sortable: true,
          },
          {
            text: 'FVC (L)',
            value: 'fvc1',
            width: `${(2.25 / 12) * 100}%`,
            sortable: true,
          },
          {
            text: 'FEV1/FVC',
            value: 'fev1FvcRatio',
            width: `${(2.25 / 12) * 100}%`,
            sortable: true,
          },
          {
            text: 'FEV1 Predicted',
            value: 'fev1Predicted',
            width: `${(2.25 / 12) * 100}%`,
            sortable: true,
          },
          {
            text: 'Test Date',
            value: 'timestamp',
            width: `${(3 / 12) * 100}%`,
            sortable: true,
          },
        ],
        items: this.medicalInfo.lungCapacities,
      }
    },
    maintenanceInhalerPrescriptionTable(): { headers: Array<any>; items: Prescription[] } {
      return {
        headers: [
          {
            text: 'Medication',
            value: 'medication',
            width: `${(4 / 12) * 100}%`,
            sortable: true,
          },
          {
            text: 'Usage per Day',
            value: 'usagePerDay',
            width: `${(4 / 12) * 100}%`,
            sortable: true,
          },
          {
            text: 'Puffs per Usage',
            value: 'puffsPerUsage',
            width: `${(4 / 12) * 100}%`,
            sortable: true,
          },
        ],
        // @ts-ignore
        items: [R.pathOr(null, ['maintenanceInhalers', 'prescriptions', 0], this.patient)].filter(Boolean),
      }
    },
    maintenanceInhalerTable(): { headers: Array<any>; items: Inhaler[] } {
      return {
        headers: [
          {
            text: 'Brand',
            value: 'brand',
            width: `${(3 / 12) * 100}%`,
            sortable: true,
          },
          {
            text: 'Doses Remaining',
            value: 'dosageRemaining',
            width: `${(3 / 12) * 100}%`,
            sortable: true,
          },
          {
            text: 'As of Date',
            value: 'asOfDate',
            width: `${(3 / 12) * 100}%`,
            sortable: true,
          },
          {
            text: 'Expiry Date',
            value: 'expiryDate',
            width: `${(3 / 12) * 100}%`,
            sortable: true,
          },
        ],
        items: R.pathOr([], ['maintenanceInhalers', 'inhalers'], this.patient),
      }
    },
    relieverInhalerPrescriptionTable(): { headers: Array<any>; items: Prescription[] } {
      return {
        headers: [
          {
            text: 'Medication',
            value: 'medication',
            width: `${(4 / 12) * 100}%`,
            sortable: true,
          },
          {
            text: 'Usage per Day',
            value: 'usagePerDay',
            width: `${(4 / 12) * 100}%`,
            sortable: true,
          },
          {
            text: 'Puffs per Usage',
            value: 'puffsPerUsage',
            width: `${(4 / 12) * 100}%`,
            sortable: true,
          },
        ],
        // @ts-ignore
        items: [R.pathOr(null, ['relieverInhalers', 'prescriptions', 0], this.patient)].filter(Boolean),
      }
    },
    relieverInhalerTable(): { headers: Array<any>; items: Inhaler[] } {
      return {
        headers: [
          {
            text: 'Brand',
            value: 'brand',
            width: `${(3 / 12) * 100}%`,
            sortable: true,
          },
          {
            text: 'Doses Remaining',
            value: 'dosageRemaining',
            width: `${(3 / 12) * 100}%`,
            sortable: true,
          },
          {
            text: 'As of Date',
            value: 'asOfDate',
            width: `${(3 / 12) * 100}%`,
            sortable: true,
          },
          {
            text: 'Expiry Date',
            value: 'expiryDate',
            width: `${(3 / 12) * 100}%`,
            sortable: true,
          },
        ],
        items: R.pathOr([], ['relieverInhalers', 'inhalers'], this.patient),
      }
    },
  },
  async mounted(): Promise<void> {
    await this.initComponent({ patientId: this.patientId, path: this.$route.path })
  },
  async beforeRouteUpdate(to, from, next): Promise<void> {
    await this.initComponent({ patientId: to.params.patient_id, path: to.path })
    next()
  },
  methods: {
    async initComponent({ patientId, path }): Promise<void> {
      this.loading = true

      await this[DetailedPatientActionNames.getDetailedPatientById]({ patientId })
      const patient = this[DetailedPatientGetterNames.patients].find(p => p.id === patientId)

      console.log({ patient })
      if (!patient) {
        await this.$router.replace({ name: '404', params: { '0': path } })
        return
      }
      this.loading = false
    },
    ...mapActions(ModuleNames.detailedPatient, [DetailedPatientActionNames.getDetailedPatientById]),
    boolToYesNo(value: any): string {
      return value ? 'Yes' : 'No'
    },
  },
})
